import { addDays, lastsWeeksFor, planBoxes, workingCadences, type Box, type BoxLine, type RefillItem, type RefillRules } from '../core/rules/refillPlan'
import type { Subscription } from '../core/types/account'
import type { Product } from '../core/types/catalog'
import type { Policy } from '../core/types/site'

/** De regels voor slim bijvullen uit de instellingen. */
export const refillRules = (sub: Policy['subscription']): RefillRules => ({
  minBoxValue: sub.minBoxValue,
  maxUnitsPerProduct: sub.maxUnitsPerProduct,
  intervals: sub.intervals,
  bufferDays: sub.bufferDays,
})

/** Producten uit de winkel als bijvulregels: net binnen, dus op over "gaat ± X mee". Onbekend valt weg. */
export function refillItemsFromProducts(products: readonly { product: Product; qty?: number }[], today: string): RefillItem[] {
  return products.flatMap(({ product, qty = 1 }) => {
    const weeks = lastsWeeksFor(product.usesPerBottle)
    return weeks === null ? [] : [{ productId: product.id, price: product.price, lastsWeeks: weeks, runsOutOn: addDays(today, weeks * 7 * qty) }]
  })
}

/**
 * Ritmes waaruit gekozen mag worden voor een lopend abonnement: wat een jaar lang het minimum haalt,
 * plus het huidige (nooit vastzetten). Haalt geen enkel ritme het (bijv. alleen producten zonder
 * "gaat ± … mee"), dan alle ritmes: dan kan het minimum niet beslissen.
 */
export function subscriptionCadences(sub: Subscription, productById: (id: string) => Product | undefined, policySub: Policy['subscription']): number[] {
  const { items, unknown } = subscriptionRefill(sub, productById, policySub, 1)
  const known = items.filter((i) => !unknown.includes(i.productId))
  const working = known.length ? workingCadences(known, refillRules(policySub), sub.nextDelivery) : []
  const base = working.length ? working : policySub.intervals
  return Array.from(new Set([...base, sub.intervalWeeks])).sort((a, b) => a - b)
}

export type SubscriptionRefill = {
  items: RefillItem[]
  /** De komende dozen; de eerste is de volgende levering. */
  boxes: Box[]
  /** Producten zonder "gaat ± X mee": die gaan elke doos mee, zoals vroeger. */
  unknown: string[]
}

/**
 * Het abonnement als slim bijvullen. Zonder op-datum (oudere data) schatten we die uit de vorige
 * levering: toen kwam het binnen, dus op na "gaat ± X mee".
 */
export function subscriptionRefill(sub: Subscription, productById: (id: string) => Product | undefined, policySub: Policy['subscription'], count = 4): SubscriptionRefill {
  const rules = refillRules(policySub)
  const lastDelivery = addDays(sub.nextDelivery, -sub.intervalWeeks * 7)
  const unknown: string[] = []
  const items: RefillItem[] = sub.items.flatMap((i) => {
    const product = productById(i.productId)
    if (!product) return []
    const known = lastsWeeksFor(product.usesPerBottle, i.usageWeeks ?? 0)
    if (known === null) unknown.push(i.productId)
    // Onbekend: elke doos één, zoals het oude abonnement.
    const lastsWeeks = known ?? sub.intervalWeeks
    const runsOutOn = i.runsOutOn ?? addDays(lastDelivery, lastsWeeks * 7)
    return [{ productId: i.productId, price: product.price, lastsWeeks, runsOutOn }]
  })
  const extras: BoxLine[] = sub.extras.flatMap((id) => {
    const p = productById(id)
    return p ? [{ productId: id, qty: 1, price: p.price }] : []
  })
  const boxes = planBoxes(items, {
    intervalWeeks: sub.intervalWeeks,
    rules,
    earliest: sub.nextDelivery,
    firstBox: sub.nextDelivery,
    skip: sub.skipNext ?? [],
    extras,
    count,
  })
  return { items, boxes, unknown }
}
